import React, { RefObject } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import MailchimpSubscribe from 'react-mailchimp-subscribe';
import type { FormHooks } from 'react-mailchimp-subscribe';

import Button, { ButtonSizes, ButtonTypes } from '@Components/Buttons/Button';
import FormField from '@Components/forms/FormField';
import ErrorDisplay from '@Components/forms/inputs/ErrorDisplay';
import TextInput, { TextInputProps } from '@Components/forms/inputs/TextInput';
import Icon, { IconSizes } from '@Components/Icon';
import Col from '@Components/layout/Col';
import Row from '@Components/layout/Row';
import Typography from '@Components/Typography';
import { useTranslations } from '@Hooks/useTranslations';
import { validate, validateEmail, validateRequired } from '@Utils/form';

import styles from './NewsletterSection.module.scss';

const mailchimpUrl =
  'https://sejico.us18.list-manage.com/subscribe/post?u=5738942ea7fd7830e17e9bdc4&amp;id=2e760c215e&amp;f_id=004e09e7f0';

type NewsletterFormData = {
  EMAIL: string;
};

const NewsletterForm = ({
  handleSubmit,
  mailChimp,
}: FormRenderProps<NewsletterFormData> & { mailChimp: FormHooks<NewsletterFormData> }) => {
  const t = useTranslations();

  const isSubmitting = mailChimp.status === 'sending';
  const isSuccess = mailChimp.status === 'success';
  const isError = mailChimp.status === 'error';

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className={styles.wrapper}>
        <Typography tag="h3" msg={t('msg_newsletter_section_title')} />

        {isSuccess ? (
          <Col constant textCenter>
            <Icon icon="green-check-circle" size={IconSizes.m} className={styles.circleIcon} />
            <Typography msg={t('msg_newsletter_section_subscribe_success')} tag="p" />
          </Col>
        ) : (
          <>
            <Typography tag="span" msg={t('msg_newsletter_section_subtitle')} className={styles.subtitle} />
            <Row constant columnMobile className={styles.inputContainer}>
              <FormField<TextInputProps<string>>
                name="EMAIL"
                component={TextInput}
                placeholder={t('msg_newsletter_section_email_input_placeholder')}
                bottomSpacing={false}
                wrapperClassname={styles.emailInput}
                validate={validate(validateEmail(t), validateRequired(t))}
              />

              <Button
                loading={isSubmitting}
                label={t('msg_subscribe_button')}
                disabled={isSubmitting}
                size={ButtonSizes.m}
                type={ButtonTypes.submit}
              />
            </Row>

            {isError && <ErrorDisplay msg={t('msg_newsletter_section_error')} className={styles.error} />}
          </>
        )}
      </div>
    </form>
  );
};

const NewsletterSection = ({ scrollRef }: { scrollRef?: RefObject<HTMLDivElement> }) => (
  <div ref={scrollRef}>
    <MailchimpSubscribe<NewsletterFormData>
      url={mailchimpUrl}
      render={mailChimp => (
        <Form<NewsletterFormData>
          onSubmit={data => mailChimp.subscribe(data)}
          initialValues={{
            EMAIL: '',
          }}
          render={props => <NewsletterForm {...props} mailChimp={mailChimp} />}
        />
      )}
    />
  </div>
);

export default NewsletterSection;
